import type { DocumentKind } from './kinds.ts';

/**
 * Подстановки в текстах документов: `{client.name}`, `{amount_words}`. У каждого вида документа свой набор
 * (раздел 5): в заявке есть клиент и сумма, в договоре с сотрудником — сотрудник. Неизвестная подстановка —
 * ошибка при сохранении шаблона, а не пустое место в подписанном документе.
 */
const STUDIO = [
  'studio.name',
  'studio.full_name',
  'studio.party',
  'studio.inn',
  'studio.kpp',
  'studio.ogrn',
  'studio.address',
  'studio.bank_name',
  'studio.bik',
  'studio.account',
  'studio.corr_account',
  'studio.signer_name',
  'studio.signer_position',
  'studio.signer_basis',
  'studio.email',
  'studio.phone',
  'studio.site',
] as const;

const CLIENT = [
  'client.name',
  'client.full_name',
  'client.party',
  'client.inn',
  'client.kpp',
  'client.ogrn',
  'client.address',
  'client.director',
  'client.director_position',
  'client.director_basis',
  'client.email',
  'client.phone',
  'client.bank_name',
  'client.bik',
  'client.account',
  'client.corr_account',
] as const;

/** Подписант клиента — контакт с отметкой «Подписывает». */
const SIGNER = ['signer.name', 'signer.position', 'signer.basis', 'signer.email'] as const;

const EMPLOYEE = [
  'employee.name',
  'employee.position',
  'employee.birth_date',
  'employee.passport',
  'employee.address',
  'employee.inn',
  'employee.snils',
  'employee.ogrnip',
  'employee.npd_date',
  'employee.bank_name',
  'employee.bik',
  'employee.account',
  'employee.email',
  'employee.phone',
] as const;

/** Номер и дата самого документа. */
const DOCUMENT = ['document.number', 'document.date'] as const;
/** Заявка, к которой относится документ (спецификация, счёт, акт, допсоглашение). */
const ORDER = ['order.number', 'order.date'] as const;
/** Договор с сотрудником, по которому составлен акт о выплате. */
const CONTRACT = ['contract.number', 'contract.date'] as const;
const OFFER = ['offer.version', 'offer.date', 'offer.url'] as const;
const MONEY = ['amount', 'amount_words', 'vat_note'] as const;

const SETS: Readonly<Record<DocumentKind, readonly string[]>> = {
  offer: [...STUDIO, ...OFFER],
  order: [...STUDIO, ...CLIENT, ...SIGNER, ...DOCUMENT, ...OFFER, ...MONEY, 'term', 'project.title'],
  specification: [...STUDIO, ...CLIENT, ...DOCUMENT, ...ORDER, ...MONEY, 'term', 'project.title'],
  invoice: [...STUDIO, ...CLIENT, ...DOCUMENT, ...ORDER, ...MONEY, 'stage'],
  act: [...STUDIO, ...CLIENT, ...SIGNER, ...DOCUMENT, ...ORDER, ...OFFER, ...MONEY, 'stage', 'project.title'],
  amendment: [
    ...STUDIO,
    ...CLIENT,
    ...SIGNER,
    ...DOCUMENT,
    ...ORDER,
    ...OFFER,
    ...MONEY,
    'term',
    'project.title',
  ],
  employee_npd: [...STUDIO, ...EMPLOYEE, ...DOCUMENT],
  employee_ip: [...STUDIO, ...EMPLOYEE, ...DOCUMENT],
  employee_gph: [...STUDIO, ...EMPLOYEE, ...DOCUMENT],
  employee_staff: [...STUDIO, ...EMPLOYEE, ...DOCUMENT],
  payout_act: [...STUDIO, ...EMPLOYEE, ...DOCUMENT, ...CONTRACT, ...MONEY, 'period'],
  // Документы сайта публикуются как оферта: версия, дата и адрес — те же подстановки.
  subscription_offer: [...STUDIO, ...OFFER],
  privacy_policy: [...STUDIO, ...OFFER],
  pd_consent: [...STUDIO, ...OFFER],
};

/** Подстановки вида документа — в порядке подсказки редактора: студия, сторона, документ, деньги. */
export const variablesOf = (kind: DocumentKind): readonly string[] => SETS[kind];

/** Все подстановки платформы: подписи в редакторе и проверка опечаток. */
export const ALL_VARIABLES: readonly string[] = [...new Set(Object.values(SETS).flat())];

/** Группа подстановки для подсказки: `client.inn` → `client`, `amount` → `document`. */
export function variableGroup(name: string): string {
  const dot = name.indexOf('.');
  return dot < 0 ? 'document' : name.slice(0, dot);
}

/** `{имя}` или `{группа.имя}`: латиница в нижнем регистре и подчёркивание. */
const TOKEN = /\{([a-z][a-z_]*(?:\.[a-z][a-z_]*)?)\}/g;

/** Подстановки в строке — по порядку, с повторами. */
export function tokensIn(text: string): string[] {
  return [...text.matchAll(TOKEN)].map((match) => match[1] ?? '');
}

/**
 * Строка с подставленными значениями. Подстановки без значения (у ИП нет КПП) — «—», чтобы в документе не
 * осталось фигурных скобок.
 */
export function fillText(text: string, values: Readonly<Record<string, string>>, missing = '—'): string {
  return text.replace(TOKEN, (_, name: string) => {
    const value = values[name];
    return value === undefined || value.trim() === '' ? missing : value;
  });
}
